import type { AxiosInstance } from "axios";
import type {
  AcceptedAiSpreadsheetOperationResponse,
  AiSpreadsheetOperation,
  AiSpreadsheetOperationResult,
} from "./types";
import {
  cancelWorkbookMutationQueue,
  enqueueWorkbookMutation,
} from "./workbook-mutation-queue";
import {
  cancelWorkbookOperationPolling,
  completeWorkbookOperation,
  runWorkbookOperationThroughConflicts,
  setWorkbookOperationRetry,
  WorkbookOperationFailedError,
  WorkbookOperationPollingError,
} from "./workbook-operation";

interface ExecuteWorkbookMutationArgs {
  api: AxiosInstance;
  businessId: string;
  runId: string;
  submit: (
    idempotencyKey: string,
  ) => Promise<AcceptedAiSpreadsheetOperationResponse>;
  refreshMode?: WorkbookRefreshMode;
  immediateSheetId?: string;
  hasPendingClientBatch?: () => boolean;
  onApplied?: (result: AiSpreadsheetOperationResult) => void;
  onQueueDrained: (context: WorkbookMutationRefreshContext) => Promise<void>;
}

export interface WorkbookMutationRefreshContext {
  changedSheetIds: string[];
  immediateSheetId?: string;
  requiresFullRefresh: boolean;
}

export type WorkbookRefreshMode = "full" | "cell-edit";

export const workbookMutationRequestConfig = (idempotencyKey: string) => ({
  headers: { "Idempotency-Key": idempotencyKey },
});

interface PendingWorkbookRefresh {
  changedSheetIds: Set<string>;
  immediateSheetId?: string;
  requiresFullRefresh: boolean;
}

const pendingRefreshByWorkbook = new Map<string, PendingWorkbookRefresh>();

const workbookKey = (businessId: string, runId: string) =>
  `${businessId}:${runId}`;

export const isRecoverableWorkbookOperationError = (error: unknown) =>
  error instanceof WorkbookOperationFailedError ||
  error instanceof WorkbookOperationPollingError;

const waitForWorkbookOperationRetry = (
  businessId: string,
  runId: string,
  signal: AbortSignal,
) =>
  new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      setWorkbookOperationRetry(businessId, runId, null);
      reject(new DOMException("Workbook mutation retry stopped", "AbortError"));
    };
    if (signal.aborted) {
      onAbort();
      return;
    }

    signal.addEventListener("abort", onAbort, { once: true });
    setWorkbookOperationRetry(businessId, runId, () => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    });
  });

const runQueuedWorkbookOperation = async ({
  api,
  businessId,
  runId,
  submit,
  signal,
}: Pick<ExecuteWorkbookMutationArgs, "api" | "businessId" | "runId"> & {
  submit: () => Promise<AcceptedAiSpreadsheetOperationResponse>;
  signal: AbortSignal;
}) => {
  const cancelPolling = () => cancelWorkbookOperationPolling(businessId, runId);
  if (signal.aborted) {
    throw new DOMException("Workbook mutation queue stopped", "AbortError");
  }

  signal.addEventListener("abort", cancelPolling, { once: true });
  try {
    return await runWorkbookOperationThroughConflicts({
      api,
      businessId,
      runId,
      submit,
      signal,
    });
  } finally {
    signal.removeEventListener("abort", cancelPolling);
  }
};

export const executeQueuedWorkbookMutation = async (
  args: ExecuteWorkbookMutationArgs,
): Promise<AiSpreadsheetOperationResult> => {
  const {
    api,
    businessId,
    runId,
    submit,
    refreshMode = "full",
    immediateSheetId,
    hasPendingClientBatch,
    onApplied,
    onQueueDrained,
  } = args;
  const key = workbookKey(businessId, runId);
  let idempotencyKey = crypto.randomUUID();

  return enqueueWorkbookMutation({
    businessId,
    runId,
    execute: async ({ hasFollowingMutations, signal }) => {
      const refreshCompletedMutations = async () => {
        if (hasFollowingMutations() || hasPendingClientBatch?.()) {
          return;
        }

        const pendingRefresh = pendingRefreshByWorkbook.get(key);
        if (!pendingRefresh) {
          completeWorkbookOperation(businessId, runId);
          return;
        }

        await onQueueDrained({
          changedSheetIds: Array.from(pendingRefresh.changedSheetIds),
          immediateSheetId: pendingRefresh.immediateSheetId,
          requiresFullRefresh: pendingRefresh.requiresFullRefresh,
        });
        if (pendingRefreshByWorkbook.get(key) === pendingRefresh) {
          pendingRefreshByWorkbook.delete(key);
        }
        if (!hasFollowingMutations() && !hasPendingClientBatch?.()) {
          completeWorkbookOperation(businessId, runId);
        }
      };

      while (true) {
        let operation: AiSpreadsheetOperation;
        try {
          operation = await runQueuedWorkbookOperation({
            api,
            businessId,
            runId,
            submit: () => submit(idempotencyKey),
            signal,
          });
        } catch (error) {
          if (!isRecoverableWorkbookOperationError(error)) {
            if (!signal.aborted) await refreshCompletedMutations();
            throw error;
          }

          await waitForWorkbookOperationRetry(businessId, runId, signal);
          if (error instanceof WorkbookOperationFailedError) {
            // A terminal failed operation is durable, so retry as a new logical
            // submission. Polling failures retain the key and resume the same one.
            idempotencyKey = crypto.randomUUID();
          }
          continue;
        }

        onApplied?.(operation.result ?? {});
        const pendingRefresh = pendingRefreshByWorkbook.get(key) ?? {
          changedSheetIds: new Set<string>(),
          requiresFullRefresh: false,
        };
        operation.result?.changed_sheet_ids?.forEach((sheetId) =>
          pendingRefresh.changedSheetIds.add(sheetId),
        );
        pendingRefresh.requiresFullRefresh ||= refreshMode === "full";
        if (immediateSheetId) {
          pendingRefresh.immediateSheetId = immediateSheetId;
        }
        pendingRefreshByWorkbook.set(key, pendingRefresh);

        // Keep later optimistic edits intact while this queue drains. The last
        // mutation performs one authoritative refresh for the whole batch.
        await refreshCompletedMutations();

        return operation.result ?? {};
      }
    },
  });
};

export const cancelQueuedWorkbookMutations = (
  businessId: string,
  runId: string,
) => {
  pendingRefreshByWorkbook.delete(workbookKey(businessId, runId));
  cancelWorkbookMutationQueue(businessId, runId);
  cancelWorkbookOperationPolling(businessId, runId);
};
